<template>
  <div v-if="numReviews > 0" class="product-reviews" id="product-reviews">
    <!-- Review overview-->
    <div class="container">
      <div class="row mb-4">
        <div class="col-sm-12">
          <p class="text-center h1">Reviews</p>
          <p class="text-center">
            Read all about the {{ productTitle }} below - we never hide low
            ratings.
          </p>
        </div>
      </div>
    </div>
    <!--review summary-->
    <div class="container">
      <div
        class="row flex-column-reverse flex-md-row mb-4"
        :class="{ 'justify-content-center': !fitDescription }"
      >
        <div
          class="col-md-6 text-center"
          :class="{ 'text-md-end': fitDescription }"
        >
          <!-- stars and distribution -->
          <div class="d-inline-block pe-4">
            <p class="display-1 lh-1 mb-0">
              {{ avgScore }}
            </p>
            <div class="text-center">
              <ReviewStars
                :numStars="avgScore"
                :numReviews="numReviews"
                size="tiny"
              />
            </div>
          </div>
          <!-- distribution graph -->
          <div class="d-inline-block">
            <ReviewDistribution
              :starDistribution="starDistribution"
              :numReviews="numReviews"
            />
          </div>
        </div>
        <div
          v-if="fitDescription"
          class="col-md-6 text-center text-md-start featured"
        >
          <p class="display-5">Customers say this fits:</p>
          <p>{{ fitDescription }}</p>
        </div>
      </div>
      <!-- write review/ask question btns -->
      <div class="row">
        <div class="col text-center">
          <a
            class="btn btn-black m-1"
            @click="askQuestion()"
            href="https://cotopaxi.kustomer.help/contact/contact-form-SktUg7Vf_"
            target="_blank"
          >
            Ask A Question
          </a>
          <a class="btn btn-default m-1" @click="writeReview()">
            Write A Review
          </a>
        </div>
      </div>
    </div>
    <!--submit review  form-->
    <div v-if="submitReviewForm">
      <ReviewForms
        @closeForm="closeReviewForm"
        :productId="productId"
        :productUrl="productUrl"
        :productTitle="productTitle"
      />
    </div>

    <!-- reviews list -->
    <div class="row mt-3">
      <div class="col review-tabs">
        <p class="display-6 text-uppercase mb-0">Reviews</p>
      </div>
    </div>

    <div class="container-fluid">
      <div class="row">
        <div class="col-md-4 col-lg-3 p-0 my-2 review-filters">
          <!--left block (fiters)-->
          <input
            v-model="searchText"
            class="w-100 p-1 my-2"
            type="text"
            placeholder="Search Reviews"
          />

          <!-- filter by rating -->
          <select v-model="searchByRatingScore" class="p-1 w-100 my-2">
            <option value="" disabled selected>Rating</option>
            <option value="0" selected>All Reviews</option>
            <option value="5">Five Stars</option>
            <option value="4">Four Stars</option>
            <option value="3">Three Stars</option>
            <option value="2">Two Stars</option>
            <option value="1">One Star</option>
          </select>

          <!-- filter by size -->
          <select
            v-if="this.isApparel || this.isKidsProduct || this.isBottoms"
            v-model="sizePurchased"
            class="p-1 w-100 my-2"
          >
            <option value="" selected disabled>Size Purchased</option>
            <option v-if="!isKidsProduct" :value="SizePurchased.xxs">
              {{ SizePurchased.xxs }}
            </option>
            <option :value="SizePurchased.xs">{{ SizePurchased.xs }}</option>
            <option :value="SizePurchased.s">{{ SizePurchased.s }}</option>
            <option :value="SizePurchased.m">{{ SizePurchased.m }}</option>
            <option :value="SizePurchased.l">{{ SizePurchased.l }}</option>
            <option :value="SizePurchased.xl">{{ SizePurchased.xl }}</option>
            <option v-if="!isKidsProduct" :value="SizePurchased.xxl">
              {{ SizePurchased.xxl }}
            </option>
            <option v-if="!isKidsProduct" :value="SizePurchased.oneX">
              {{ SizePurchased.oneX }}
            </option>
            <option v-if="!isKidsProduct" :value="SizePurchased.twoX">
              {{ SizePurchased.twoX }}
            </option>
            <option v-if="!isKidsProduct" :value="SizePurchased.threeX">
              {{ SizePurchased.threeX }}
            </option>
            <option v-if="!isKidsProduct" :value="SizePurchased.fourX">
              {{ SizePurchased.fourX }}
            </option>
          </select>

          <!-- filter by height (adult) -->
          <select
            v-if="isApparel || isBottoms"
            v-model="customerHeight"
            class="p-1 w-100 my-2"
          >
            <option value="" selected disabled>Customer Height</option>
            <option :value="CustomerHeight.underFourFootSix">
              {{ CustomerHeight.underFourFootSix }}
            </option>
            <option :value="CustomerHeight.fourFootsixToFiveFoot">
              {{ CustomerHeight.fourFootsixToFiveFoot }}
            </option>
            <option :value="CustomerHeight.fiveFootToFiveFootSix">
              {{ CustomerHeight.fiveFootToFiveFootSix }}
            </option>
            <option :value="CustomerHeight.fiveFootSixToSixFoot">
              {{ CustomerHeight.fiveFootSixToSixFoot }}
            </option>
            <option :value="CustomerHeight.sixFootToSixFootSix">
              {{ CustomerHeight.sixFootToSixFootSix }}
            </option>
            <option :value="CustomerHeight.overSixFootSix">
              {{ CustomerHeight.overSixFootSix }}
            </option>
          </select>

          <!-- filter by height (kids) -->
          <select
            v-if="isKidsProduct"
            v-model="kidHeight"
            class="p-1 w-100 my-2"
          >
            <option value="" selected disabled>Child's Height</option>
            <option :value="KidsHeight.underThreeSix">
              {{ KidsHeight.underThreeSix }}
            </option>
            <option :value="KidsHeight.threeSixToFourFoot">
              {{ KidsHeight.threeSixToFourFoot }}
            </option>
            <option :value="KidsHeight.fourFootToFourSix">
              {{ KidsHeight.fourFootToFourSix }}
            </option>
            <option :value="KidsHeight.fourSixToFiveFoot">
              {{ KidsHeight.fourSixToFiveFoot }}
            </option>
            <option :value="KidsHeight.fiveFootToFiveSix">
              {{ KidsHeight.fiveFootToFiveSix }}
            </option>
            <option :value="KidsHeight.overFiveSix">
              {{ KidsHeight.overFiveSix }}
            </option>
          </select>

          <!-- Adult weight filter -->
          <select
            v-if="isApparel || isBottoms"
            v-model="customerWeight"
            class="p-1 w-100 my-2"
          >
            <option value="" selected disabled>Customer Weight</option>
            <option :value="CustomerWeight.below100">
              {{ CustomerWeight.below100 }}
            </option>
            <option :value="CustomerWeight.max125">
              {{ CustomerWeight.max125 }}
            </option>
            <option :value="CustomerWeight.max145">
              {{ CustomerWeight.max145 }}
            </option>
            <option :value="CustomerWeight.max165">
              {{ CustomerWeight.max165 }}
            </option>
            <option :value="CustomerWeight.max185">
              {{ CustomerWeight.max185 }}
            </option>
            <option :value="CustomerWeight.max200">
              {{ CustomerWeight.max200 }}
            </option>
            <option :value="CustomerWeight.max220">
              {{ CustomerWeight.max220 }}
            </option>
            <option :value="CustomerWeight.over220">
              {{ CustomerWeight.over220 }}
            </option>
          </select>

          <!-- Kid weight filter -->
          <select
            v-if="isKidsProduct"
            v-model="kidWeight"
            class="p-1 w-100 my-2"
          >
            <option value="" selected disabled>Child's Weight</option>
            <option :value="KidsWeight.under40">
              {{ KidsWeight.under40 }}
            </option>
            <option :value="KidsWeight.under60">
              {{ KidsWeight.under60 }}
            </option>
            <option :value="KidsWeight.under80">
              {{ KidsWeight.under80 }}
            </option>
            <option :value="KidsWeight.under100">
              {{ KidsWeight.under100 }}
            </option>
            <option :value="KidsWeight.under120">
              {{ KidsWeight.under120 }}
            </option>
            <option :value="KidsWeight.over120">
              {{ KidsWeight.over120 }}
            </option>
          </select>

          <select v-if="isApparel" v-model="bodyType" class="p-1 w-100 my-2">
            <option value="" selected disabled>Body Type</option>
            <option :value="BodyType.triangle">
              {{ BodyType.triangle }}
            </option>
            <option :value="BodyType.invertedTriangle">
              {{ BodyType.invertedTriangle }}
            </option>
            <option :value="BodyType.rectangle">
              {{ BodyType.rectangle }}
            </option>
            <option :value="BodyType.oval">
              {{ BodyType.oval }}
            </option>
            <option :value="BodyType.hourglass">
              {{ BodyType.hourglass }}
            </option>
          </select>

          <!-- body type filter for bags (different options than apparel) -->
          <select v-if="isPack" v-model="bodyBuild" class="p-1 w-100 my-2">
            <option value="" selected disabled>Body Type</option>
            <option :value="BodyBuild.petite">
              {{ BodyBuild.petite }}
            </option>
            <option :value="BodyBuild.average">
              {{ BodyBuild.average }}
            </option>
            <option :value="BodyBuild.big">
              {{ BodyBuild.big }}
            </option>
          </select>

          <!-- body type filter for bottoms (different options than apparel/bags) -->
          <select
            v-if="isBottoms"
            v-model="bodyTypeBottoms"
            class="p-1 w-100 my-2"
          >
            <option value="" selected disabled>Body Type</option>
            <option :value="BodyTypeBottoms.wideHips">
              {{ BodyTypeBottoms.wideHips }}
            </option>
            <option :value="BodyTypeBottoms.smallHips">
              {{ BodyTypeBottoms.smallHips }}
            </option>
            <option :value="BodyTypeBottoms.highWaist">
              {{ BodyTypeBottoms.highWaist }}
            </option>
            <option :value="BodyTypeBottoms.shortWaist">
              {{ BodyTypeBottoms.shortWaist }}
            </option>
            <option :value="BodyTypeBottoms.longLegs">
              {{ BodyTypeBottoms.longLegs }}
            </option>
            <option :value="BodyTypeBottoms.shortLegs">
              {{ BodyTypeBottoms.shortLegs }}
            </option>
          </select>

          <select v-model="pictured" class="p-1 w-100 my-2">
            <option value="" selected disabled>Images & Videos</option>
            <option :value="false">All</option>
            <option :value="true" selected>With Images & Videos</option>
          </select>

          <!-- clear filters button -->
          <p
            v-if="
              searchText ||
              searchByRatingScore ||
              sizePurchased ||
              customerHeight ||
              kidHeight ||
              customerWeight ||
              kidWeight ||
              bodyType ||
              bodyBuild ||
              bodyTypeBottoms ||
              pictured
            "
            @click="clearFilters()"
            class="reset"
          >
            <small><u>Reset Filter</u></small>
          </p>
        </div>
        <!-- right block (reviews-list)-->
        <div class="col-md-8 col-lg-9 p-0 px-md-3">
          <LoadingAnimation v-if="loading"></LoadingAnimation>

          <div
            class="d-flex justify-content-between align-items-center my-3"
            v-if="!loading"
          >
            <p class="display-6 mb-0">
              {{ numFilteredReviews }}
              {{ numFilteredReviews == 1 ? 'Review' : 'Reviews' }}
            </p>
            <div class="no-focus-outline">
              <span>Sort:</span>
              <select class="p-1 border-0" v-model="sortMethod">
                <option :value="Sort.includesImg">Reviews with Images</option>
                <option :value="Sort.newest">Newest</option>
                <option :value="Sort.highestRating">Highest Rating</option>
                <option :value="Sort.lowestRating">Lowest Rating</option>
                <option :value="Sort.votesMost">Most Helpful</option>
                <option :value="Sort.votesLeast">Least Helpful</option>
              </select>
            </div>
          </div>

          <!-- Reviews -->
          <div v-if="!loading">
            <div
              v-for="review in filteredReviews.slice(
                (this.page - 1) * resultsPerPage,
                (this.page - 1) * resultsPerPage + 3
              )"
              :key="review.id"
            >
              <ReviewCard :rev="review" />
              <hr />
            </div>

            <!-- Load more btn -->
            <div
              v-if="!showAllReviews && numFilteredReviews > 3"
              class="m-4 text-center"
            >
              <button
                @click="loadMoreReviews"
                type="button"
                role="tab"
                aria-label="Click the button to write a review"
                class="btn btn-default"
              >
                <span>Load More</span>
              </button>
            </div>
          </div>

          <!-- additional reviews after 'load more' is clicked -->
          <div v-if="showAllReviews && !loading">
            <div
              v-for="review in filteredReviews.slice(
                (this.page - 1) * resultsPerPage + 3,
                (this.page - 1) * resultsPerPage + 10
              )"
              :key="review.id"
            >
              <ReviewCard :rev="review" />
              <hr />
            </div>
            <div v-if="numFilteredReviews > 10">
              <ReviewPagination
                :page="page"
                :totalNumResults="numFilteredReviews"
                :filteredNumResults="numFilteredReviews"
                :resultsFiltered="reviewsFiltered"
                :resultsPerPage="resultsPerPage"
                @changePage="(newPage) => changePage(newPage)"
              />
            </div>
          </div>

          <!-- no results found -->
          <div v-if="reviewsFiltered && numFilteredReviews == 0 && !loading">
            <p>Sorry, no reviews match your search.</p>
            <p class="reset" @click="clearFilters()"><u>See All Reviews</u></p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="numReviews == 0" class="text-center mb-4">
    <h2>Reviews</h2>
    <p>No reviews yet. Be the first to leave a review!</p>
    <div class="row">
      <div class="col text-center">
        <a
          class="btn btn-black m-1"
          @click="askQuestion()"
          href="https://cotopaxi.kustomer.help/contact/contact-form-SktUg7Vf_"
          target="_blank"
        >
          Ask A Question
        </a>
        <a class="btn btn-default m-1" @click="writeReview()">
          Write A Review
        </a>
      </div>
    </div>
  </div>
  <div v-if="submitReviewForm">
    <ReviewForms
      @closeForm="closeReviewForm"
      :productId="productId"
      :productUrl="productUrl"
      :productTitle="productTitle"
    />
  </div>

  <div v-else-if="error" class="product-reviews">
    <h2>Reviews</h2>
    <p>Oops, something went wrong. Please try again later</p>
  </div>
  <div v-else></div>
</template>

<script src="./ctrl-yotpo-reviews.js"></script>
